import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Fab,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Tooltip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { Search } from '@mui/icons-material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CreateIcon from '@mui/icons-material/Create';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { KeyboardArrowUp } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { RootState } from '../../reducers';
import classes from './Styles';
import { fetchRoles } from '../../store/shared.slice';
import { copyObject } from '../../utils/common';
import {
    generateAssessmentWorkflowPDF,
    getDocumentEngagementByMemberId,
    getMeansOfEngagements,
    getReasonForVisit,
    reopenEngagement,
    resetDownloadVerificationModal,
    toggleShowDocEngagementModal,
} from '../../store/documentEngagement.slice';
import { DocumentEngagement } from '../../Models/DocumentEngagements/DocumentEngagement.model';
import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../utils/dateHelpers';
import Authorization from '../../utils/Authorization';
import AddOrUpdateEngagementDetail from './AddOrUpdateEngagementDetail';
import AlertDialog from '../AlertDialog/AlertDialog';
import { PartTwoDownloadEnum } from '../../Enum/PartTwoDownloadEnum';
import { useNavigate, useParams } from 'react-router-dom';
import { MemberStatusEnum } from '../../Enum/MemberStatusEnum';
import { DocumentEngagementType } from '../../Enum/DocumentEngagementTypeEnum';

const DocumentEngagements = (props: any) => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const [searchKey, setSearchKey] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const memberId = props.memberId || params.memberId;
    const { roles } = useSelector((state: RootState) => state.shared);
    const { documentEngagements, downloadVerificationModal, meansOfEngagements, isFetchingDocumentEngagements, generatingPdfEngagementIds, isSaving } =
        useSelector((state: RootState) => state.documentEngagement);
    const { chartDetails } = useSelector((state: RootState) => state.memberDemographicsDetails);
    const authData = useMemo(() => new Authorization(), []);
    const canAddEditEngagements = useMemo(() => authData.isInPermission('Add / Edit engagements'), [authData]);
    const [engagementToReopen, setEngagementToReopen] = useState<string | null>(null);

    const [showScrollerButton, setShowScrollerButton] = useState(false);
    const scrollToTop = React.useCallback(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const scrolled = document.documentElement.scrollTop;
            setShowScrollerButton(scrolled > 300);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        dispatch(fetchRoles());
        dispatch(getMeansOfEngagements());
        dispatch(getDocumentEngagementByMemberId(memberId));
        dispatch(getReasonForVisit());
    }, [memberId, dispatch]);

    const handleOnChangeRole = (value: any) => {
        setSelectedRole(value);
    };

    const generatePDF = (engagementId: string) => {
        dispatch(generateAssessmentWorkflowPDF(engagementId));
    };

    const handleOnClickProcedureSignOff = (engagementId: string) => {
        navigate(`/provider/claims-management`);
    };

    const handleConfirmReopen = () => {
        if (!engagementToReopen || !memberId) return;

        dispatch(
            reopenEngagement(
                engagementToReopen,
                navigate,
                `/provider/members-list/member-chart/${memberId}/assessment-dashboard/assessment/${engagementToReopen}`,
                () => setEngagementToReopen(null)
            )
        );
    };

    const meansOfEngagementsById = useMemo(() => {
        const map = new Map<string, string>();
        meansOfEngagements.forEach((m) => {
            if (m.Id !== undefined && m.Id !== null) {
                map.set(String(m.Id), m.Name || '');
            }
        });
        return map;
    }, [meansOfEngagements]);

    const preparedDocEngagements = useMemo(() => {
        let data = ((copyObject(documentEngagements) || []) as DocumentEngagement[]).filter(
            (d) => Boolean(d.EngagedProvider?.Name) || Boolean(d.ReasonForVisit?.Name)
        );
        if (Boolean(searchKey)) {
            const searchKeyData = searchKey.toLowerCase();
            data = data.filter((d) => {
                const meansLabel = meansOfEngagementsById.get(String(d.MeansOfEngagement)) || '';
                return (
                    meansLabel.toLowerCase().includes(searchKeyData) ||
                    d.ReasonForVisit?.Name?.toLowerCase().includes(searchKeyData) ||
                    d.EngagedProvider?.Name?.toLowerCase().includes(searchKeyData) ||
                    d.VisitedTime?.toLowerCase().includes(searchKeyData) ||
                    d.CreationTime?.toLowerCase().includes(searchKeyData) ||
                    d.Notes?.toLowerCase().includes(searchKeyData)
                );
            });
        }

        if (Boolean(selectedRole)) {
            data = data.filter((d) => d.AssessedRole?.RoleName === selectedRole);
        }

        return data;
    }, [documentEngagements, meansOfEngagementsById, searchKey, selectedRole]);

    const engagementColumns: GridColDef<DocumentEngagement>[] = [
        {
            field: 'MeansOfEngagement',
            headerName: 'Means of Engagement',
            flex: 0.8,
            minWidth: 180,
            valueGetter: (_, row) => meansOfEngagementsById.get(String(row.MeansOfEngagement)) || '-',
        },
        {
            field: 'ReasonForVisit',
            headerName: 'Reason for Engagement',
            flex: 0.8,
            minWidth: 160,
            valueGetter: (_, row) => row.ReasonForVisit?.Name || '-',
        },
        {
            field: 'Contacted',
            headerName: 'Contacted',
            width: 110,
            valueGetter: (_, row) => (row.Contacted ? 'Yes' : 'No'),
        },
        {
            field: 'EngagedProvider',
            headerName: 'Engaged Provider',
            flex: 0.8,
            minWidth: 160,
            valueGetter: (_, row) => row.EngagedProvider?.Name || '-',
        },
        {
            field: 'VisitedTime',
            headerName: 'Engagement Date',
            width: 170,
            valueGetter: (_, row) => {
                const date = toDate(row.VisitedTime);
                return date && isValid(date) ? formatDate(date, 'MM/dd/yyyy hh:mm a') : '-';
            },
        },
        {
            field: 'CreationTime',
            headerName: 'Creation Date',
            width: 170,
            valueGetter: (_, row) => {
                const date = toDate(row.CreationTime);
                return date && isValid(date) ? formatDate(date, 'MM/dd/yyyy hh:mm a') : '-';
            },
        },
        {
            field: 'Notes',
            headerName: 'Notes',
            flex: 1.5,
            minWidth: 180,
            renderCell: (params) => <div dangerouslySetInnerHTML={{ __html: params.row.Notes || '' }} />,
        },
        {
            field: 'actions',
            headerName: '',
            width: canAddEditEngagements ? 150 : 55,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const docEngagement = params.row;
                const isGenerating = generatingPdfEngagementIds.some((id) => id === docEngagement.Id);
                const isAssessmentEngagement =
                    Boolean(docEngagement.AssessedRole) ||
                    docEngagement.Type === DocumentEngagementType.Draft ||
                    docEngagement.Type === DocumentEngagementType.Submitted;
                const canEdit = !isAssessmentEngagement || docEngagement.CanReopen === true;

                return (
                    <Stack direction="row">
                        {canAddEditEngagements && authData.UserId === docEngagement.EngagedProvider?.Id ? (
                            <Tooltip
                                title={
                                    canEdit ? 'Edit' : 'Only engagements with all encounters For Review and no submitted claim can be reopened.'
                                }
                                placement="left"
                            >
                                <span>
                                    <IconButton
                                        aria-label="Edit engagement"
                                        disabled={!canEdit}
                                        onClick={() => {
                                            if (isAssessmentEngagement) {
                                                setEngagementToReopen(docEngagement.Id);
                                            } else {
                                                dispatch(toggleShowDocEngagementModal(true, docEngagement));
                                            }
                                        }}
                                    >
                                        <CreateIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : null}
                        {canAddEditEngagements ? (
                            <Tooltip title="Procedures / Sign Off" placement="left">
                                <IconButton onClick={() => handleOnClickProcedureSignOff(docEngagement.Id)}>
                                    <FactCheckOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        <Tooltip title="Generate PDF" placement="left">
                            <IconButton onClick={() => generatePDF(docEngagement.Id)} disabled={isGenerating}>
                                {isGenerating ? <CircularProgress size={18} /> : <PictureAsPdfOutlinedIcon />}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={classes.container}>
                <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" columnGap={2} width="50%" maxWidth="700px">
                        <FormControl style={{ width: '50%' }}>
                            <InputLabel shrink id="role-select-label">
                                Role
                            </InputLabel>
                            <Select
                                labelId="role-select-label"
                                label="Role"
                                displayEmpty
                                value={selectedRole}
                                input={<OutlinedInput notched label="Role" />}
                                onChange={(event) => handleOnChangeRole(event.target.value)}
                            >
                                <MenuItem value={''}>All Roles</MenuItem>
                                {roles
                                    .filter((r) => r.Id)
                                    .map((type, index) => (
                                        <MenuItem key={`${type.Id}-${index}`} value={type.Name}>
                                            {type.Name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <FormControl style={{ minWidth: '50%' }}>
                            <InputLabel htmlFor="outlined-adornment-password">Search</InputLabel>
                            <OutlinedInput
                                id="outlined-adornment-password"
                                type="text"
                                value={searchKey}
                                onChange={(e) => setSearchKey(e.target.value as string)}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton aria-label="Search" edge="end">
                                            <Search />
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Search"
                            />
                        </FormControl>
                    </Stack>
                    <Button
                        variant="outlined"
                        disabled={chartDetails.Status === MemberStatusEnum.Undetermined || chartDetails.Status === MemberStatusEnum.Scheduled}
                        onClick={() => dispatch(toggleShowDocEngagementModal(true))}
                    >
                        Document Engagement
                    </Button>
                </Stack>
                <Box sx={{ width: '100%', marginTop: '20px' }}>
                    <DataGrid
                        autoHeight
                        rows={preparedDocEngagements}
                        columns={engagementColumns}
                        getRowId={(row) => row.Id}
                        getRowHeight={() => 'auto'}
                        loading={isFetchingDocumentEngagements}
                        disableRowSelectionOnClick
                        pageSizeOptions={[50, 100, 200]}
                        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                        localeText={{ noRowsLabel: `No Document Engagement${searchKey ? ' found' : ''}` }}
                    />
                </Box>
                <AddOrUpdateEngagementDetail memberId={memberId} />
            </Box>
            <AlertDialog
                open={downloadVerificationModal?.Show}
                title="Part 2 consent verification"
                isLoading={generatingPdfEngagementIds.some((id) => id === downloadVerificationModal.EngagementId)}
                okText={String(PartTwoDownloadEnum.WithPartTwoDataDescription)}
                skipText={String(PartTwoDownloadEnum.WithoutPartTwoDataDescription)}
                cancelText="Cancel"
                buttonsDirection="column-reverse"
                skipButtonVariant="contained"
                onConfirm={() => dispatch(resetDownloadVerificationModal({ ...downloadVerificationModal, Show: false, ShowVerificationOptions: true }))}
                onSkip={() => dispatch(generateAssessmentWorkflowPDF(downloadVerificationModal.EngagementId, PartTwoDownloadEnum.WithoutPartTwoData))}
                onClose={() => dispatch(resetDownloadVerificationModal())}
                message={'This assessment contains Part 2 data, which cannot be shared without appropriate Part 2 consent. Do you want to'}
            />
            <AlertDialog
                open={downloadVerificationModal?.ShowVerificationOptions}
                title="Part 2 consent verification"
                isLoading={generatingPdfEngagementIds.some((id) => id === downloadVerificationModal.EngagementId)}
                okText={String(PartTwoDownloadEnum.VerifiedDescription)}
                skipText={String(PartTwoDownloadEnum.InternalUseDescription)}
                cancelText={String(PartTwoDownloadEnum.NoneDescription)}
                buttonsDirection="column-reverse"
                skipButtonVariant="contained"
                onConfirm={() => dispatch(generateAssessmentWorkflowPDF(downloadVerificationModal.EngagementId, PartTwoDownloadEnum.Verified))}
                onSkip={() => dispatch(generateAssessmentWorkflowPDF(downloadVerificationModal.EngagementId, PartTwoDownloadEnum.InternalUse))}
                onClose={() => dispatch(resetDownloadVerificationModal())}
                message={downloadVerificationModal?.Message}
            />
            <AlertDialog
                open={engagementToReopen !== null}
                title="Move assessment to drafts?"
                message="This saved-for-review assessment will be moved to Draft. It will no longer appear in Document Engagements or Claims Management while you edit it. Continue?"
                okText="Move to Drafts and Edit"
                isLoading={isSaving}
                onConfirm={handleConfirmReopen}
                onClose={() => setEngagementToReopen(null)}
            />
            {showScrollerButton && (
                <Fab onClick={scrollToTop} sx={classes.scrollTopButton} color="primary" size="small">
                    <KeyboardArrowUp />
                </Fab>
            )}
        </LocalizationProvider>
    );
};

export default DocumentEngagements;
